import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { businessInfo, zones } from "../data/content";
import { getBreadcrumbJsonLd } from "../data/jsonld";

export default function Zones() {
  return (
    <>
      <SEO
        title="Zones d'intervention - Plombier à Meaux et alentours (77) | Pereira"
        description="Nous intervenons à Meaux (77) et dans un rayon de 25 km : Esbly, Trilport, Lizy-sur-Ourcq, Crégy-les-Meaux, Quincy-Voisins. Dépannage rapide."
        jsonLd={getBreadcrumbJsonLd([
          { name: "Accueil", url: "/" },
          { name: "Zones d'intervention" },
        ])}
      />

      {/* Header */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-accent font-medium tracking-widest text-secondary uppercase mb-2">Proximité</p>
          <h1 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight" data-testid="zones-title">
            Zones d'intervention
          </h1>
          <p className="text-slate-300 mt-4 max-w-2xl text-lg">
            Nous intervenons rapidement dans un rayon de 25 km autour de Meaux, notamment dans les communes suivantes.
          </p>
        </div>
      </section>

      {/* Zones Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6">
                Communes desservies
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Notre entreprise est basée à Meaux (77100). Nous nous déplaçons directement chez vous pour toutes vos interventions en plomberie et chauffage. Que ce soit pour un dépannage urgent ou un projet programmé, nous couvrons l'ensemble du secteur de Meaux et ses environs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {zones.map((zone) => (
                  <div
                    key={zone.name}
                    className={`flex items-center gap-3 p-4 rounded-sm border transition-all ${
                      zone.primary
                        ? "bg-secondary/5 border-secondary"
                        : "bg-white border-slate-200 hover:border-secondary/50"
                    }`}
                    data-testid={`zone-${zone.name.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <MapPin size={16} className={zone.primary ? "text-secondary" : "text-slate-400"} />
                    <div>
                      <p className={`font-medium text-sm ${zone.primary ? "text-secondary font-bold" : ""}`}>{zone.name}</p>
                      {zone.zip && <p className="text-xs text-slate-400">{zone.zip}</p>}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3 p-4 rounded-sm border border-dashed border-slate-300">
                  <MapPin size={16} className="text-slate-400" />
                  <p className="text-sm text-slate-500">Et bien d'autres communes...</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Map placeholder */}
              <div className="bg-primary rounded-sm overflow-hidden h-64 flex items-center justify-center">
                <div className="text-center text-white p-6">
                  <MapPin size={48} className="mx-auto text-secondary mb-3" />
                  <p className="font-heading text-lg font-bold uppercase">Meaux et alentours</p>
                  <p className="text-slate-400 text-sm">Rayon de 25 km</p>
                </div>
              </div>

              {/* Info cards */}
              <div className="bg-white p-6 border-l-4 border-secondary rounded-r-md shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <Clock size={20} className="text-secondary" />
                  <h3 className="font-heading text-lg font-semibold uppercase">Délai d'intervention</h3>
                </div>
                <p className="text-sm text-slate-600">
                  En cas d'urgence, nous faisons notre maximum pour intervenir dans les meilleurs délais, souvent dans la journée. Pour les travaux programmés, nous fixons un rendez-vous à votre convenance.
                </p>
              </div>

              <div className="bg-white p-6 border-l-4 border-primary rounded-r-md shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <Phone size={20} className="text-primary" />
                  <h3 className="font-heading text-lg font-semibold uppercase">Votre commune n'est pas listée ?</h3>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Contactez-nous pour vérifier si nous intervenons dans votre secteur. Nous faisons toujours notre possible pour répondre à votre demande.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-1 text-secondary text-sm font-bold uppercase tracking-wider">
                  Nous contacter <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
